import React from 'react';
import { useTranslation } from 'react-i18next';

import { TooltipContainer } from '~/components/tooltip-container/TooltipContainer';
import { PosthogTrackingSource } from '~/services/tracking/posthog/constants';

import { CatalystInsightIcon } from '../catalyst-insight-icon/CatalystInsightIcon';
import { getCatalystInsightIconProps } from '../catalyst-insight-icon/utils';
import { CatalystInsightTooltip } from '../catalyst-insight-tooltip/CatalystInsightTooltip';
import { useCatalystDrawer } from '../useCatalystDrawer';
import * as S from './CatalystInsightPill.styles';

export interface CatalystInsightPillProps {
  insightId: string;
  entityTag?: string;
  themeId?: string;
  type: 'company' | 'theme';
  isClickable?: boolean;
  source: PosthogTrackingSource;
}

export const CatalystInsightPill = ({
  insightId,
  entityTag,
  themeId,
  type,
  isClickable = false,
  source,
}: CatalystInsightPillProps) => {
  const { t } = useTranslation(['insights']);
  const { openDrawer } = useCatalystDrawer();

  const handleClick = () => {
    if (isClickable) {
      if (entityTag) {
        openDrawer({ type: 'company', entityTag, insightId, source });
      } else if (themeId) {
        openDrawer({ type: 'theme', themeId, insightId, source });
      }
    }
  };

  const isCompanyCatalyst = type === 'company';

  return (
    <TooltipContainer
      label={
        <CatalystInsightTooltip
          insightId={insightId}
          type={type}
          iconProps={getCatalystInsightIconProps({
            isBordered: false,
            isCompanyCatalyst,
          })}
        />
      }
      placement="top-start"
      offsetY={-5}
      offsetX={8}
      hasMaxWidth={false}
      disabled={!isClickable}
      inPortal
      withSafePolygon
    >
      <S.CatalystInsightWrapper
        data-testid="catalyst-insight-pill"
        $isClickable={isClickable}
        $isCompanyCatalyst={isCompanyCatalyst}
        onClick={handleClick}
      >
        <CatalystInsightIcon
          size={12}
          enableHover={false}
          isBordered={false}
          catalystInsightId={insightId}
          type={type}
          source={source}
        />
        <S.CatalystInsightLabel $isCompanyCatalyst={isCompanyCatalyst}>
          {isCompanyCatalyst
            ? t('insights:catalyst.titleCompanyCatalyst')
            : t('insights:catalyst.titleMarketCatalyst')}
        </S.CatalystInsightLabel>
      </S.CatalystInsightWrapper>
    </TooltipContainer>
  );
};
